// src/components/Assessment/QuestionRenderer.jsx

import React from 'react';
import {
  VStack,
  Heading,
  Text,
  Button,
  RadioGroup,
  Stack,
  Radio,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Input,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';

const QuestionRenderer = ({
  question,
  answers,
  updateAnswer,
  onNext,
  onPrev,
  currentStep,
  totalSteps,
  emailRef
}) => {
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const placeholderColor = useColorModeValue("gray.500", "gray.400");

  const renderQuestionContent = () => {
    switch (question.type) {
      case 'intro':
        return (
          <VStack align="start" spacing={5}>
            <Heading size="lg" color={textColor}>{question.title}</Heading>
            <Text color={textColor}>{question.description}</Text>
            <Button colorScheme="green" onClick={onNext}>
              {question.nextButtonText || 'Start'}
            </Button>
          </VStack>
        );

      case 'radio':
        return (
          <VStack align="start" spacing={6}>
            <Heading size="md" color={textColor}>{question.title}</Heading>
            {question.description && (
              <Text color={textColor} fontSize="sm">{question.description}</Text>
            )}
            <RadioGroup
              value={answers[question.field]}
              onChange={(value) => updateAnswer(question.field, value)}
            >
              <Stack>
                {question.options.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
            <NavigationButtons 
              onPrev={onPrev}
              onNext={onNext}
              isNextDisabled={!answers[question.field]}
            />
          </VStack>
        );

      case 'slider':
        return (
          <VStack align="start" spacing={6}>
            <Heading size="md" color={textColor}>{question.title}</Heading>
            {question.description && (
              <Text color={textColor} fontSize="sm">{question.description}</Text>
            )}
            <Slider
              min={question.min}
              max={question.max}
              step={question.step}
              value={answers[question.field]}
              onChange={(value) => updateAnswer(question.field, value)}
            >
              <SliderTrack>
                <SliderFilledTrack bg="green.500" />
              </SliderTrack>
              <SliderThumb />
            </Slider>
            <Text color={textColor} fontWeight="medium">
              {getSliderLabel(question, answers[question.field])}
            </Text>
            <NavigationButtons 
              onPrev={onPrev}
              onNext={onNext}
            />
          </VStack>
        );

      case 'contact':
        return (
          <VStack align="start" spacing={6}>
            <Heading size="md" color={textColor}>{question.title}</Heading>
            <Text color={textColor}>{question.description}</Text>
            {question.fields.map((field) => (
              <Input
                key={field.id}
                placeholder={field.placeholder}
                type={field.type}
                value={answers[field.id] || ''}
                onChange={(e) => updateAnswer(field.id, e.target.value)}
                borderColor={borderColor}
                color={textColor}
                _placeholder={{ color: placeholderColor }}
                ref={field.type === 'email' ? emailRef : null}
                onFocus={(e) => e.target.focus()}
              />
            ))}
            <NavigationButtons 
              onPrev={onPrev}
              onNext={onNext}
              isNextDisabled={!isContactFormValid(question, answers)}
            />
          </VStack>
        );

      default:
        return <Text color={textColor}>Unknown question type</Text>;
    }
  };

  return renderQuestionContent();
};

const NavigationButtons = ({ onPrev, onNext, isNextDisabled = false }) => (
  <HStack>
    <Button onClick={onPrev}>Back</Button>
    <Button
      colorScheme="green"
      isDisabled={isNextDisabled}
      onClick={onNext}
    >
      Next
    </Button>
  </HStack>
);

const getSliderLabel = (question, value) => {
  if (question.labels && question.labels[value]) {
    return `${question.labels[value]} (${value})`;
  }
  return `Value: ${value}`;
};

const isContactFormValid = (question, answers) => {
  return question.fields.every(field => {
    if (field.required) {
      return answers[field.id] && answers[field.id].trim() !== '';
    }
    return true;
  });
};

export default QuestionRenderer;